import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import CreateWizardLayout from '../../../../../components/CreateWizardLayout';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../constants/event-items';
import { sortEventAddonItemsByPriority } from '../../util';
import EventAddonItemBlock from '../FreemiumEventCreateSKUSelectionStep/components/EventAddonItemBlock';
import useExperimentVariant from '../../../../../../../services/experiments/useExperimentVariant';
import { EXPERIMENT_KEY } from '../../../../../../../services/experiments/experiments';
import { FREEMIUM_EVENT_CREATE_SKU_UPSELL_STEP_VARIANTS } from '../../../../../../shutterbug/views/SignupAlbumCreate/helpers';
import { ALBUM_SUBTYPE } from '../../../../../../../constants/albums';
import WaldoSpeechBubble from '../../../../../../../components/global/WaldoSpeechBubble';

const propTypes = {
  setStepProps: PropTypes.func.isRequired,
  stepProps: PropTypes.shape({
    eventAddonItems: PropTypes.func,
    onEventAddonItemToggle: PropTypes.func,
    currentSelectedEventAddonItemIds: PropTypes.arrayOf(PropTypes.string),
  }),
};

const FreemiumEventCreateUpgradeStep = ({ stepProps, setStepProps }) => {
  const variant = useExperimentVariant(
    EXPERIMENT_KEY.FREEMIUM_EVENT_CREATE_SKU_UPSELL_STEP,
    FREEMIUM_EVENT_CREATE_SKU_UPSELL_STEP_VARIANTS.VARIANT_A,
  );

  const {
    eventType,
    eventAddonItems,
    currentSelectedEventAddonItemIds,
    onEventAddonItemToggle,
    selectedEventAddonItemIds = [],
    eventAddonItemTypesUpsell = [],
    standaloneEventAddonType,
    includePhotosAlbumDefault,
  } = stepProps;

  const availableEventAddonItems = sortEventAddonItemsByPriority(
    eventAddonItems.filter(eventAddonItem => {
      // In general gold upgrade will now be handled via Subscription
      // upsell if necessary - and we consider it necessary if the original
      // query had `includePhotosAlbum
      if (
        includePhotosAlbumDefault &&
        eventAddonItem.types.length === 1 &&
        eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED
      ) {
        return false;
      }
      // Ensure it hasn't been pre-selected by default
      return (
        eventAddonItem.types.length === 1 &&
        !selectedEventAddonItemIds.includes(eventAddonItem.id) &&
        !eventAddonItemTypesUpsell.includes(eventAddonItem.types[0])
      );
    }),
  );

  // Experiment related:
  const squareVariants = [
    FREEMIUM_EVENT_CREATE_SKU_UPSELL_STEP_VARIANTS.VARIANT_C,
    FREEMIUM_EVENT_CREATE_SKU_UPSELL_STEP_VARIANTS.VARIANT_D,
  ];

  const isSquaredVariant = squareVariants.includes(variant);

  const VariantWrapper = ({ children }) =>
    isSquaredVariant ? (
      <div className="two-column-grid">{children}</div>
    ) : (
      <>{children}</>
    );

  const baseTitle = (() => {
    const addons = [];
    if (
      availableEventAddonItems.find(
        ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED,
      )
    ) {
      addons.push('gold album');
    }

    if (
      [ALBUM_SUBTYPE.WEDDINGS, ALBUM_SUBTYPE.MEMORIALS].includes(eventType) &&
      availableEventAddonItems.find(
        ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      )
    ) {
      addons.push('video guestbook');
    } else if (
      availableEventAddonItems.find(
        ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
      )
    ) {
      addons.push('group video');
    }

    if (
      availableEventAddonItems.find(
        ({ types }) => types[0] === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
      )
    ) {
      addons.push('memories slideshow');
    }

    if (addons.length === 1) {
      return `Make your event more memorable with a ${addons[0]}!`;
    }

    return `Make your event more memorable with a ${addons
      .slice(0, addons.length - 1)
      .join(',')} or ${addons[addons.length - 1]}!`;
  })();

  return (
    <CreateWizardLayout.Wrapper>
      <CreateWizardLayout.Content>
        {[
          EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
          EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
        ].includes(standaloneEventAddonType) ? (
          <h2>
            {eventType === ALBUM_SUBTYPE.MEMORIALS
              ? 'Having a memorial service?'
              : 'Having a party?'}
          </h2>
        ) : (
          <WaldoSpeechBubble
            className="freemium-create-wizard-bubble"
            position="right"
          >
            {baseTitle}
          </WaldoSpeechBubble>
        )}
        <VariantWrapper>
          {availableEventAddonItems.map(eventAddonItem => {
            const title = (() => {
              if (eventType === ALBUM_SUBTYPE.WEDDINGS) {
                if (
                  standaloneEventAddonType ===
                    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
                  eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED
                ) {
                  return (
                    <p className="text-dark">
                      Want to add a live slideshow at the wedding? Waldo-fy it
                      and collect and deliver everyone&apos;s party pics!
                    </p>
                  );
                }
                if (
                  standaloneEventAddonType ===
                    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
                  eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
                ) {
                  return (
                    <p className="text-dark">
                      Take a trip down memory lane with Waldo&apos;s Memories
                      Slideshow! Great for rehearsal dinners!
                    </p>
                  );
                }
              }

              if (eventType === ALBUM_SUBTYPE.MEMORIALS) {
                if (
                  [
                    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
                    EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
                  ].includes(standaloneEventAddonType) &&
                  eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED
                ) {
                  return (
                    <p className="text-dark">
                      Let Waldo help you gather more photos of friends &amp;
                      family.
                    </p>
                  );
                }

                if (
                  standaloneEventAddonType ===
                    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
                  eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED
                ) {
                  return (
                    <p className="text-dark">
                      Having a reception and want to collect and share
                      everyone&apos;s photos from the day?
                    </p>
                  );
                }
                if (
                  standaloneEventAddonType ===
                    EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
                  eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
                ) {
                  return (
                    <p className="text-dark">
                      Want to play a slideshow at the memorial? Let Waldo do the
                      work of collecting and organizing old photos from friends
                      and family.
                    </p>
                  );
                }

                if (
                  standaloneEventAddonType ===
                    EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK &&
                  eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO
                ) {
                  return (
                    <p className="text-dark">
                      Let Waldo collect video messages from family &amp; friends
                      and turn them into a Video Guestbook?
                    </p>
                  );
                }
              }

              if (
                [
                  EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO,
                  EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK,
                ].includes(standaloneEventAddonType) &&
                eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.WALDOFIED
              ) {
                return (
                  <p className="text-dark">
                    Waldo-fy it and collect and deliver everyone&apos;s party
                    pics!
                  </p>
                );
              }

              if (
                standaloneEventAddonType ===
                  EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO &&
                eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK
              ) {
                return (
                  <p className="text-dark">
                    Let Waldo collect old photos from family &amp; friends and
                    turn them into a Memories Slideshow?
                  </p>
                );
              }

              if (
                standaloneEventAddonType ===
                  EVENT_ADDON_ITEM_TYPE.MEMORY_BOOK &&
                eventAddonItem.types[0] === EVENT_ADDON_ITEM_TYPE.GROUP_VIDEO
              ) {
                return (
                  <p className="text-dark">
                    Let Waldo collect video messages from family &amp; friends
                    and turn them into a Group Video?
                  </p>
                );
              }

              return <></>;
            })();

            return (
              <Fragment key={eventAddonItem.id}>
                {title}
                <EventAddonItemBlock
                  onToggle={() => {
                    onEventAddonItemToggle(eventAddonItem.id);
                    if (!stepProps.upgradeStepCompleted) {
                      setStepProps({
                        ...stepProps,
                        upgradeStepCompleted: true,
                      });
                    }
                    if (
                      eventAddonItem.types[0] ===
                      EVENT_ADDON_ITEM_TYPE.WALDOFIED
                    ) {
                      setStepProps({
                        ...stepProps,
                        upgradeStepCompleted: true,
                        // Toggle
                        includePhotosAlbum: !currentSelectedEventAddonItemIds.includes(
                          eventAddonItem.id,
                        ),
                      });
                    }
                  }}
                  isSelected={currentSelectedEventAddonItemIds.includes(
                    eventAddonItem.id,
                  )}
                  eventAddonItem={eventAddonItem}
                  eventType={eventType}
                  itemStyle={isSquaredVariant ? 'squared' : undefined}
                  standaloneEventAddonType={standaloneEventAddonType}
                />
              </Fragment>
            );
          })}
        </VariantWrapper>
      </CreateWizardLayout.Content>
    </CreateWizardLayout.Wrapper>
  );
};

FreemiumEventCreateUpgradeStep.propTypes = propTypes;

export default FreemiumEventCreateUpgradeStep;
